<script lang="ts" setup>
import { SvgIcon } from '@progress/kendo-vue-common';
import { computed, onMounted, ref } from 'vue';

import FontAwesomeIcon from '@/components/elements/FontAwesomeIcon.vue';
import { container } from '@/container';
import { t } from '@/locales';
import { Router } from '@/services/router/Router';
import { INavbarItem } from '~/INavbarItem';

// Props.
interface Props {
  dataTestid?: string;
  item: INavbarItem;
}

const router = container.get(Router);

const props = withDefaults(defineProps<Props>(), {
  dataTestid: 'navbar_menu_item',
});

// Emits.
const emit = defineEmits(['click']);

// Refs.
const title = ref('');

// Computed.
const item = computed(() => props.item);
const isOpen = computed(() => item.value.open);
const route = computed((() => item.value.route));
const attributes = computed((() => route.value.meta?.attributes));
const icon = computed((() => route.value.meta?.icon));
const hasChildren = computed(() => route.value.children?.length > 0);
const hideLabel = computed(() => !!route.value.meta?.hideLabel);

onMounted(async () => {
  title.value = await router.getRouteTitle(route.value);
});
</script>

<template>
  <li
    :class="{
      'navbar-menu-item': true,
      open: isOpen,
      'has-icon': !!icon,
    }"
    :data-testid="`${props.dataTestid}_item`"
    @click="emit('click', item)"
  >
    <slot name="before" />
    <RouterLink
      :to="route"
      custom
      v-slot="{ navigate, isActive, isExactActive }"
    >
      <component
        v-bind="attributes"
        :is="hasChildren ? 'span' : 'a'"
        :class="{
          'navbar-menu-item--link': true,
          'has-submenu': hasChildren,
          [`icon-${icon?.position}`]: icon?.position,
          'navbar-menu-item--link--active': isExactActive,
          'navbar-menu-item--link--active-trail': isActive,
        }"
        :data-testid="`${props.dataTestid}_link`"
        :href="!hasChildren ? route?.redirect ?? route?.path : undefined"
        :target="route?.meta?.newWindow ? '_blank' : undefined"
        @click="!(hasChildren || route?.meta?.external) && navigate($event)"
        v-tooltip.bottom="hideLabel ? { content: t('pages.configuration.label'), distance: '-5' } : undefined"
      >
        <SvgIcon
          v-if="icon?.type === 'svg' && icon?.position === 'before'"
          :class="`${props.dataTestid}--icon`"
          :icon="{name: icon.data.name, content: icon.data.data.data, viewBox: icon.data.data.viewBox}"
          :style="{
            width: icon.size ?? 16,
            height: icon.size ?? 16,
            color: icon.color
          }"
        />
        <FontAwesomeIcon
          v-else-if="icon?.position === 'before' || (icon?.position === undefined && icon?.name)"
          :name="icon.name"
          :type="icon.type"
          :size="icon.size"
        />
        <span
          v-if="!hideLabel"
          class="navbar-menu-item--label"
        >{{ title }}</span>
        <SvgIcon
          v-if="icon?.type === 'svg' && icon?.position === 'after'"
          :class="`${props.dataTestid}--icon`"
          :icon="{name: icon.data.name, content: icon.data.data.data, viewBox: icon.data.data.viewBox}"
          :style="{
            width: icon.size ?? 16,
            height: icon.size ?? 16,
            color: icon.color
          }"
        />
        <FontAwesomeIcon
          v-else-if="icon?.position === 'after'"
          :name="icon.name"
          :type="icon.type"
          :size="icon.size"
        />
        <span
          v-if="hasChildren && !icon"
          class="navbar-menu-item--more fal fa-angle-down fa-fw font-normal"
        />
      </component>
    </RouterLink>
    <slot name="after" />
  </li>
</template>

<style lang="scss">
.navbar-menu-item {
  position: relative;

  .navbar-menu-item--link {
    color: inherit !important;
    cursor: pointer;
    display: block;
    opacity: .8;
    padding: var(--space-small) var(--space-small);
    position: relative;
    text-decoration: none;
    transition: 300ms ease-out;
    white-space: nowrap;

    &:hover {
      opacity: 1;
    }

    &.has-submenu::after {
      background: var(--app-color);
      bottom: 0;
      content: '';
      height: .3em;
      left: 0;
      opacity: 0;
      position: absolute;
      right: 0;
      transition: 300ms ease-out;
      transform: scaleX(0);
    }

    &--active::before {
      content: '\f0da'; // caret-right
      font-weight: 900;
      font-family: 'Font Awesome 5 Pro';
      font-size: var(--text-size-medium);
      color: var(--app-color);
      display: inline-block;
      position: absolute;
      left: var(--space-min);
      top: 50%;
      transform: translate(0%, -50%);
    }

    &--active-trail.has-submenu {
      &::after {
        opacity: 1;
        transform: scaleX(1);
      }
    }

    &.app-label {
      display: inline-block;
      font-weight: 700;
      text-transform: uppercase;
      &::before,
      &::after {
        display: none;
      }
    }
  }
  .navbar-menu-item--label {
    margin-left: var(--space-xsmall);
  }

  &.open .navbar-menu-item--link {
    background-color: var(--gray-lighten-5);
    &::after {
      opacity: 1;
      transform: scaleX(1);
    }
  }

}

.has-icon > .navbar-menu-item--link {
  padding: var(--space-small);
}
</style>
